import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  image_url: string | null;
  published_at: string;
  created_at: string;
  author_name: string | null;
  tags: string[] | null;
}

export default function Blog() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    const { data } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('status', 'published')
      .order('published_at', { ascending: false });
    
    setPosts(data || []);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-7xl py-12 px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="text-3xl font-semibold text-foreground">
            Our Blog Posts
          </h1>
        </div>

        {posts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground text-lg">No blog posts yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article 
                key={post.id} 
                className="group cursor-pointer border border-border rounded-xl overflow-hidden hover:shadow-lg transition-shadow bg-card"
                onClick={() => navigate(`/blog/${post.id}`)}
              >
                {/* Image */}
                <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
                  {post.image_url ? (
                    <img 
                      src={post.image_url} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      No image
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  {/* Author and Date */}
                  <div className="text-sm text-muted-foreground mb-2">
                    {post.author_name && (
                      <>
                        <span className="font-medium text-foreground">{post.author_name}</span>
                        <span className="mx-2">•</span>
                      </>
                    )}
                    <span>{formatDate(post.published_at)}</span>
                  </div>

                  {/* Title with Arrow */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h2 className="text-xl font-semibold text-foreground group-hover:text-primary transition-colors leading-tight">
                      {post.title}
                    </h2>
                    <ArrowUpRight className="h-5 w-5 flex-shrink-0 text-foreground mt-1 group-hover:text-primary transition-colors" />
                  </div>

                  {/* Excerpt */}
                  <p className="text-muted-foreground line-clamp-3 mb-4 leading-relaxed">
                    {post.excerpt}
                  </p>

                  {/* Tags */}
                  {post.tags && post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {post.tags.map((tag, idx) => (
                        <Badge 
                          key={idx} 
                          variant="outline" 
                          className="rounded-full font-normal text-sm px-3 py-1"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
